import { useState } from 'react';
import {
  useDeleteModuleDataMutation,
  useRemoveContentMutation,
  useUpdateVideoContentMutation,
  useUpdateContentMutation,
  ContentFragmentDoc, ExtendedUnitWithHeaFragmentDoc
} from '@api/graphql-codegen';
import { useEditUnit } from '../useEditUnit';
import { Lesson, ContentType, DefaultModuleEnum, ExtendedContent } from '@types';
import { logger } from '../../../../../utils/logger';

export interface useSchoolModuleCRUDProps {
  currentTabContent?: ExtendedContent;
  groups: ExtendedContent[];
  clearFile: () => void;
  activeTab: string;
  isGroupContent: boolean;
  onChangeData: (v: string | null) => void;
  onChangeGroupTabs: (v: ExtendedContent[]) => void;
};

export const useSchoolModuleCRUD = ({ currentTabContent, groups, clearFile, activeTab, isGroupContent, onChangeData, onChangeGroupTabs }: useSchoolModuleCRUDProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { content, lesson, setSelectionLevel, unit, setUnit } = useEditUnit();

  const [deleteModuleData, { loading: deletingModuleData }] = useDeleteModuleDataMutation({
    update(cache, res) {
      if (res.data?.deleteModuleData) {
        cache.updateFragment({
          id: `Content:${currentTabContent?.id}`,
          fragment: ContentFragmentDoc,
        }, (c) => ({ ...c, data: null }))
        onChangeData(null);
        const newGroups = [...groups];
        newGroups[0].data = null;
        onChangeGroupTabs(newGroups);
      }
    }
  });

  const [removeContent, { loading: isRemovingModule }] = useRemoveContentMutation({
    update(_cache, res) {
      setLoading(false);
      if (res.data?.removeContent) {
        if (unit) {
          if (lesson?.id) {
            const classPresentation = [...(unit.classPresentation || [])]
            const lessonIndex = classPresentation.findIndex(m => m?.id === lesson?.id);
            if (lessonIndex > -1) {
              const selectedLesson = classPresentation[lessonIndex];
              const modules = [...(selectedLesson?.modules || [])];
              const moduleIndex = modules.findIndex(m => m?.id === content?.id);
              if (moduleIndex > -1) {
                modules.splice(moduleIndex, 1);
                classPresentation[lessonIndex] = {...selectedLesson, modules: modules} as Lesson;
              }
              const materials = [...(selectedLesson?.materials || [])];
              const materialsIndex = materials.findIndex(m => m?.id === content?.id);
              if (materialsIndex > -1) {
                materials.splice(materialsIndex, 1);
                classPresentation[lessonIndex] = {...selectedLesson, materials: materials} as Lesson;
              }
              if (selectedLesson?.sections?.length) {
                for (const section of selectedLesson.sections) {
                  const sectionModuleIndex = section.modules?.findIndex(m => m?.id === content?.id);
                  if (sectionModuleIndex! > -1) {
                    section.modules?.splice(sectionModuleIndex!, 1);
                    break;
                  }
                }
              }
              // classPresentation[lessonIndex] = {...selectedLesson, modules} as Lesson;
            }
            setUnit({...unit, classPresentation});
            _cache.updateFragment({
              id: `Unit:${unit?.id}`,
              fragment: ExtendedUnitWithHeaFragmentDoc,
              fragmentName: 'ExtendedUnitWithHEA'
            }, (data) =>({
              ...data,
              classPresentation,
            }));
          } else {
            const teacherPreparation = [...(unit.teacherPreparation || [])];
            const index = teacherPreparation.findIndex(m => m?.id === content?.id);
            if (index > -1) {
              teacherPreparation.splice(index, 1);

              setUnit({...unit, teacherPreparation});
              _cache.updateFragment({
                id: `Unit:${unit?.id}`,
                fragment: ExtendedUnitWithHeaFragmentDoc,
                fragmentName: 'ExtendedUnitWithHEA'
              }, (data) =>({
                ...data,
                teacherPreparation,
              }));
            }
            const studyGuides = [...(unit.studyGuides || [])];
            const indexOfStudyGuides = studyGuides.findIndex(m => m?.id === content?.id);
            if (indexOfStudyGuides > -1) {
              studyGuides.splice(indexOfStudyGuides, 1);

              setUnit({...unit, studyGuides});
              _cache.updateFragment({
                id: `Unit:${unit?.id}`,
                fragment: ExtendedUnitWithHeaFragmentDoc,
                fragmentName: 'ExtendedUnitWithHEA'
              }, (data) =>({
                ...data,
                studyGuides,
              }));
            }
            const studyGuideMaterials = [...(unit?.studyGuideMaterials || [])];
            const indexOfStudyGuideMaterials = studyGuideMaterials.findIndex(m => m?.id === content?.id);
            if (indexOfStudyGuideMaterials > -1) {
              studyGuideMaterials.splice(indexOfStudyGuideMaterials, 1);

              setUnit({...unit, studyGuideMaterials});
              _cache.updateFragment({
                id: `Unit:${unit?.id}`,
                fragment: ExtendedUnitWithHeaFragmentDoc,
                fragmentName: 'ExtendedUnitWithHEA'
              }, (data) =>({
                ...data,
                studyGuideMaterials,
              }));
            }
            const studentQuizzes = [...(unit.studentQuizzes || [])];
            const indexOfStudentQuizzes = studentQuizzes.findIndex(m => m?.id === content?.id);
            if (indexOfStudentQuizzes > -1) {
              studentQuizzes.splice(indexOfStudentQuizzes, 1);

              setUnit({...unit, studentQuizzes});
              _cache.updateFragment({
                id: `Unit:${unit?.id}`,
                fragment: ExtendedUnitWithHeaFragmentDoc,
                fragmentName: 'ExtendedUnitWithHEA'
              }, (data) =>({
                ...data,
                studentQuizzes,
              }));
            }
          }
        }
      }
    },
    onCompleted() {
      if (lesson?.id) {
        setSelectionLevel('Lesson');
      } else {
        setSelectionLevel('Unit');
      }
    },
    onError(e) {
      setLoading(false);
      logger.info({ e });
    },
  });



  const [updateVideoContent] = useUpdateVideoContentMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      setLoading(false);
    },
    update(cache, res) {
      const d = res?.data?.updateVideoContent;
      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: {
            ...d,
          },
          fragment: ContentFragmentDoc,
        });
        if (d?.data) {
          onChangeData(d?.data)
        }
      }
      clearFile();
    },
    onCompleted() {
      setLoading(false);
    }
  });

  const [updateContent] = useUpdateContentMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      setLoading(false);
    },
    update(cache, res) {
      const d = res.data?.updateContent;

      if (d) {
        cache.updateFragment({
          id: d.id,
          fragment: ContentFragmentDoc,
        }, (data) =>({
          ...data, name: d.name,
        }));
        if (isGroupContent) {
          setLoading(false);
          return;
        }
        if (d.type !== ContentType.FORM) {
          onChangeData(d?.data ?? '');
        }
        const newGroupsTabs = [...groups];
        const index = newGroupsTabs.findIndex(a => a.groupName === activeTab);
        if (d.type === ContentType.FORM && activeTab !== DefaultModuleEnum.Default) {
          newGroupsTabs[index] = { ...newGroupsTabs[index], name: d.name, data: newGroupsTabs[index].data, meta: d.meta, description: d.description, formData: d.formData, position: d.position, lastInGroup: d.lastInGroup, lastInLesson: d.lastInLesson, isRequired: d.isRequired };
        } else {
          if (activeTab === DefaultModuleEnum.Default) {
            newGroupsTabs[index] = { ...newGroupsTabs[index], ...d };
          } else {
            newGroupsTabs[index] = { ...newGroupsTabs[index], name: d.name, meta: d.meta, description: d.description, formData: d.formData, position: d.position, lastInGroup: d.lastInGroup, lastInLesson: d.lastInLesson, isRequired: d.isRequired };
          }
        }
        onChangeGroupTabs(newGroupsTabs);
        clearFile();
        if (unit) {
          if (lesson?.id) {
            const classPresentation = [...(unit.classPresentation || [])]
            const lessonIndex = classPresentation.findIndex(m => m?.id === lesson?.id);
            if (lessonIndex > -1) {
              const selectedLesson = classPresentation[lessonIndex];
              var modules = [...(selectedLesson?.modules || [])];
              const moduleIndex = modules.findIndex(m => m?.id === d?.id);
              if (d.additionalAfter) {
                modules = modules.map((module, index) => {
                  if (module.type !== ContentType.FORM) {
                    return {
                      ...module,
                      additionalAfter: false
                    }
                  }
                  return module
                })
              }
              if (d.isRequired) {
                modules = modules.map((module, index) => {
                  if (module.type !== ContentType.FORM) {
                    return {
                      ...module,
                      isRequired: false
                    }
                  }
                  return module
                })
              }
              if (d.lastInLesson) {
                modules = modules.map((module, index) => {
                  if (module.type !== ContentType.FORM) {
                    return {
                      ...module,
                      lastInLesson: false
                    }
                  }
                  return module
                })
              }
              if (moduleIndex > -1) {
                modules[moduleIndex] = d;
              }
              classPresentation[lessonIndex] = {...selectedLesson, modules} as Lesson;
              const materials = [...(selectedLesson?.materials || [])];
              const materialsIndex = materials.findIndex(m => m?.id === d?.id);
              if (materialsIndex > -1) {
                materials[materialsIndex] = d;
                classPresentation[lessonIndex] = {...selectedLesson, materials} as Lesson;
              }
            }
            setUnit({...unit, classPresentation});
            cache.updateFragment({
              id: `Unit:${unit?.id}`,
              fragment: ExtendedUnitWithHeaFragmentDoc,
              fragmentName: 'ExtendedUnitWithHEA'
            }, (data) =>({
              ...data,
              classPresentation,
            }));
          } else {
            const teacherPreparation = [...(unit.teacherPreparation || [])]
            const index = teacherPreparation.findIndex(m => m?.id === d?.id);
            if (index > -1) {
              teacherPreparation[index] = d;
            }
            setUnit({...unit, teacherPreparation});
            cache.updateFragment({
              id: `Unit:${unit?.id}`,
              fragment: ExtendedUnitWithHeaFragmentDoc,
              fragmentName: 'ExtendedUnitWithHEA'
            }, (data) =>({
              ...data,
              teacherPreparation,
            }));
          }
        }
      }
      setLoading(false);
    }
  });


  return {
    updateContent,
    updateVideoContent,
    deleteModuleData,
    removeContent,
    loading,
    isRemovingModule,
    deletingModuleData
  }
};
